import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Fab,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  Skeleton,
} from "@mui/material";
import {
  Search,
  Add,
  Edit,
  Delete,
  MoreVert,
  Category as CategoryIcon,
  FilterList,
  ViewModule,
  ViewList,
  Apps,
  SortByAlpha,
  DateRange,
  TrendingUp,
} from "@mui/icons-material";
import { LoadingSpinner } from "../components";
import { supabaseApi as api } from "../services/supabaseApi";
import { Category, CategoryFormData } from "@catalog/types";
import { useDebounce } from "../hooks/useDebounce";

// Category Form Component
interface CategoryFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CategoryFormData) => void;
  initialData?: Category;
  mode?: "create" | "edit";
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  mode = "create",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState<CategoryFormData>({
    name: initialData?.name || "",
    description: initialData?.description || "",
  });

  React.useEffect(() => {
    if (open) {
      setFormData({
        name: initialData?.name || "",
        description: initialData?.description || "",
      });
    }
  }, [open, initialData]);

  const handleInputChange = (field: keyof CategoryFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ name: "", description: "" });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile} // Full screen on mobile
    >
      <DialogTitle
        sx={{
          pb: { xs: 1, md: 2 }, // Reduced padding on mobile
          px: { xs: 2, md: 3 }, // Reduced horizontal padding on mobile
        }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: { xs: 36, md: 40 }, // Smaller avatar on mobile
              height: { xs: 36, md: 40 },
            }}
          >
            <CategoryIcon sx={{ fontSize: { xs: 18, md: 24 } }} />
          </Avatar>
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: "1.125rem", md: "1.25rem" } }} // Smaller title on mobile
          >
            {mode === "create" ? "Create New Category" : "Edit Category"}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, md: 3 }, // Reduced horizontal padding on mobile
          pt: { xs: 1, md: 2 }, // Reduced top padding on mobile
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ pt: { xs: 1, md: 2 } }}
        >
          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {" "}
            {/* Reduced spacing on mobile */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
                placeholder="e.g., Furniture, Electronics, Decorations"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CategoryIcon
                        color="action"
                        sx={{ fontSize: { xs: 18, md: 20 } }} // Smaller icon on mobile
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                multiline
                rows={isMobile ? 3 : 4} // Fewer rows on mobile
                placeholder="Brief description of this category and what items it contains..."
                variant="outlined"
                helperText="Optional: Provide context about what types of items belong in this category"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, md: 3 }, // Reduced horizontal padding on mobile
          pb: { xs: 2, md: 3 }, // Reduced bottom padding on mobile
          gap: { xs: 1, md: 0 }, // Add gap on mobile
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
            px: { xs: 2, md: 3 }, // Reduced padding on mobile
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name.trim()}
          startIcon={mode === "create" ? <Add /> : <Edit />}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
            px: { xs: 2, md: 3 }, // Reduced padding on mobile
          }}
        >
          {mode === "create" ? "Create Category" : "Update Category"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete Confirmation Dialog
interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  category: Category | null;
  itemCount?: number;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  category,
  itemCount = 0,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const canDelete = itemCount === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile} // Full screen on mobile
    >
      <DialogTitle sx={{ pb: { xs: 1, md: 2 }, px: { xs: 2, md: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
          <Avatar
            sx={{
              bgcolor: "error.main",
              width: { xs: 36, md: 40 },
              height: { xs: 36, md: 40 },
            }}
          >
            <Delete sx={{ fontSize: { xs: 18, md: 20 } }} />
          </Avatar>
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: "1.125rem", md: "1.25rem" } }}
          >
            Confirm Delete
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, md: 3 }, pt: { xs: 1, md: 2 } }}>
        <Stack spacing={{ xs: 1.5, md: 2 }}>
          <Typography sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>
            Are you sure you want to delete the category "{category?.name}"?
          </Typography>
          {!canDelete && (
            <Alert
              severity="warning"
              sx={{
                fontSize: { xs: "0.75rem", md: "0.875rem" },
                "& .MuiAlert-icon": {
                  fontSize: { xs: 18, md: 22 },
                },
              }}
            >
              This category contains {itemCount} items. You must move or delete
              these items before deleting the category.
            </Alert>
          )}
          {canDelete && (
            <Alert
              severity="error"
              sx={{
                fontSize: { xs: "0.75rem", md: "0.875rem" },
                "& .MuiAlert-icon": {
                  fontSize: { xs: 18, md: 22 },
                },
              }}
            >
              This action cannot be undone.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          pb: { xs: 2, md: 3 },
          gap: { xs: 1, md: 0 },
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 2, md: 3 },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={!canDelete}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 2, md: 3 },
          }}
        >
          Delete Category
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Category Stats Card
interface CategoryStatsProps {
  category: Category;
  itemCount: number;
  onEdit: () => void;
  onDelete: () => void;
}

const CategoryCard: React.FC<CategoryStatsProps> = ({
  category,
  itemCount,
  onEdit,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit();
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete();
    handleMenuClose();
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: { xs: "none", sm: "translateY(-4px)" }, // Disable hover animation on mobile
          boxShadow: { xs: theme.shadows[2], sm: theme.shadows[8] },
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        {" "}
        {/* Reduced mobile padding */}
        <Stack spacing={{ xs: 1.5, md: 2 }}>
          {" "}
          {/* Reduced mobile spacing */}
          {/* Header with Icon and Category Info */}
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={{ xs: 1.5, md: 2 }}
          >
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                width: { xs: 36, md: 48 }, // Smaller avatar on mobile
                height: { xs: 36, md: 48 },
              }}
            >
              <Apps sx={{ fontSize: { xs: 18, md: 24 } }} />{" "}
              {/* Smaller icon on mobile */}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 0.5, md: 1 }, // Reduced margin on mobile
                  lineHeight: 1.2,
                  fontSize: { xs: "1rem", md: "1.25rem" }, // Smaller text on mobile
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {category.name}
              </Typography>

              {category.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: { xs: 2, md: 3 }, // Fewer lines on mobile
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.4,
                    fontSize: { xs: "0.75rem", md: "0.875rem" }, // Smaller text on mobile
                  }}
                >
                  {category.description}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                opacity: 0.7,
                "&:hover": { opacity: 1 },
                padding: { xs: 0.5, md: 1 }, // Smaller touch target on mobile
              }}
            >
              <MoreVert sx={{ fontSize: { xs: 18, md: 20 } }} />
            </IconButton>
          </Stack>
          {/* Stats */}
          <Stack
            direction="row"
            spacing={{ xs: 1, md: 2 }}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              icon={<TrendingUp sx={{ fontSize: { xs: 14, md: 16 } }} />}
              label={`${itemCount} items`}
              size="small"
              color={itemCount > 0 ? "primary" : "default"}
              variant="outlined"
              sx={{
                height: { xs: 24, md: 32 }, // Smaller chip on mobile
                fontSize: { xs: "0.65rem", md: "0.75rem" },
                "& .MuiChip-label": {
                  px: { xs: 1, md: 1.5 },
                },
              }}
            />
            <Chip
              icon={<DateRange sx={{ fontSize: { xs: 14, md: 16 } }} />}
              label={new Date(category.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: isMobile ? "2-digit" : "numeric", // Shorter date format on mobile
              })}
              size="small"
              variant="outlined"
              sx={{
                height: { xs: 24, md: 32 }, // Smaller chip on mobile
                fontSize: { xs: "0.65rem", md: "0.75rem" },
                "& .MuiChip-label": {
                  px: { xs: 1, md: 1.5 },
                },
              }}
            />
          </Stack>
        </Stack>
      </CardContent>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: 2, fontSize: { xs: "1rem", md: "1.2rem" } }} />
          <Typography sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>
            Edit Category
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <Delete sx={{ mr: 2, fontSize: { xs: "1rem", md: "1.2rem" } }} />
          <Typography sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>
            Delete Category
          </Typography>
        </MenuItem>
      </Menu>
    </Card>
  );
};

// Main Categories Page Component
const CategoriesPage: React.FC = () => {
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "created" | "items">("name");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const queryClient = useQueryClient();
  const debouncedSearch = useDebounce(searchValue, 300);

  // Fetch categories and items
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: api.categories.getAll,
  });

  const { data: items } = useQuery({
    queryKey: ["items"],
    queryFn: api.items.getAll,
  });

  // Calculate item counts per category
  const categoryItemCounts = useMemo(() => {
    if (!items) return {};
    return items.reduce(
      (acc, item) => {
        acc[item.category_id] = (acc[item.category_id] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [items]);

  // Filter and sort categories
  const filteredAndSortedCategories = useMemo(() => {
    if (!categories) return [];

    let filtered = categories.filter(
      (category) =>
        category.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (category.description
          ?.toLowerCase()
          .includes(debouncedSearch.toLowerCase()) ??
          false)
    );

    // Sort categories
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "created":
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case "items":
          const aCount = categoryItemCounts[a.id] || 0;
          const bCount = categoryItemCounts[b.id] || 0;
          return bCount - aCount;
        default:
          return 0;
      }
    });

    return filtered;
  }, [categories, debouncedSearch, sortBy, categoryItemCounts]);

  // Event handlers
  const handleAddCategory = () => {
    setCategoryToEdit(null);
    setIsFormOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setCategoryToEdit(category);
    setIsFormOpen(true);
  };

  const handleDeleteCategory = (category: Category) => {
    setCategoryToDelete(category);
    setIsDeleteDialogOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setCategoryToEdit(null);
  };

  const handleFormSubmit = async (formData: CategoryFormData) => {
    try {
      if (categoryToEdit) {
        // Update existing category
        await api.categories.update(categoryToEdit.id, formData);
        setSnackbar({
          open: true,
          message: `Successfully updated "${formData.name}" category!`,
          severity: "success",
        });
      } else {
        // Create new category
        await api.categories.create(formData);
        setSnackbar({
          open: true,
          message: `Successfully created "${formData.name}" category!`,
          severity: "success",
        });
      }

      // Invalidate queries
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
      setIsFormOpen(false);
      setCategoryToEdit(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to ${categoryToEdit ? "update" : "create"} category. Please try again.`,
        severity: "error",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      await api.categories.delete(categoryToDelete.id);

      // Invalidate queries
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
      await queryClient.invalidateQueries({ queryKey: ["items"] });

      setSnackbar({
        open: true,
        message: `Successfully deleted "${categoryToDelete.name}" category!`,
        severity: "success",
      });

      setIsDeleteDialogOpen(false);
      setCategoryToDelete(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete category. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (categoriesLoading) {
    return <LoadingSpinner message="Loading categories..." />;
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      <Container maxWidth="xl" sx={{ py: { xs: 1.5, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 1.5, md: 4 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: "text.primary",
                  letterSpacing: "-0.02em",
                  fontSize: { xs: "1.5rem", md: "2.125rem" },
                }}
              >
                Categories
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontWeight: 400 }}
              >
                Organize and manage your inventory categories
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCategory}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                display: { xs: "none", sm: "flex" },
              }}
            >
              Add Category
            </Button>
          </Stack>
        </Box>

        {/* Search and Controls */}
        <Paper
          sx={{
            p: { xs: 1.5, md: 3 },
            mb: { xs: 1.5, md: 3 },
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search categories..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { sm: 300 }, flex: 1 }}
            />

            <Stack direction="row" spacing={1}>
              <TextField
                select
                label="Sort by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                size="small"
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="items">Item Count</MenuItem>
              </TextField>

              <IconButton
                onClick={() =>
                  setViewMode(viewMode === "grid" ? "list" : "grid")
                }
                sx={{ ml: 1 }}
              >
                {viewMode === "grid" ? <ViewList /> : <ViewModule />}
              </IconButton>
            </Stack>
          </Stack>
        </Paper>

        {/* Categories Grid */}
        {filteredAndSortedCategories.length === 0 ? (
          <Paper
            sx={{ p: { xs: 3, md: 6 }, textAlign: "center", borderRadius: 2 }}
          >
            <CategoryIcon
              sx={{
                fontSize: { xs: 48, md: 64 },
                color: "text.disabled",
                mb: 2,
              }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchValue ? "No categories found" : "No categories yet"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {searchValue
                ? "Try adjusting your search terms"
                : "Create your first category to start organizing your inventory"}
            </Typography>
            {!searchValue && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddCategory}
                sx={{
                  fontSize: { xs: "0.8125rem", md: "0.875rem" },
                  py: { xs: 0.75, md: 1 },
                }}
              >
                Create Category
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {filteredAndSortedCategories.map((category) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
                <CategoryCard
                  category={category}
                  itemCount={categoryItemCounts[category.id] || 0}
                  onEdit={() => handleEditCategory(category)}
                  onDelete={() => handleDeleteCategory(category)}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Floating Action Button for Mobile */}
        <Fab
          color="primary"
          onClick={handleAddCategory}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            display: { xs: "flex", sm: "none" },
          }}
        >
          <Add />
        </Fab>

        {/* Category Form Dialog */}
        <CategoryForm
          open={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          initialData={categoryToEdit || undefined}
          mode={categoryToEdit ? "edit" : "create"}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          category={categoryToDelete}
          itemCount={
            categoryToDelete ? categoryItemCounts[categoryToDelete.id] || 0 : 0
          }
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CategoriesPage;
